import { useMemo } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ListEnvelope } from '../../../shared/api/genexus-query';
import type { CrudApi, CrudHooks } from '../../../shared/api/crud-hooks';
import type { DataTableQuery } from '../../../shared/components/DataTable';
import { createEmployeeTaxasApi } from './api';
import type { EmployeeTaxaDto, EmployeeTaxaInput, EmployeeTaxaListItem } from './types';

/**
 * Hooks CRUD para Taxas de um Colaborador específico.
 * Espelha `useCompanyAddressesHooks` — wiring manual com `useMemo` porque a
 * `queryKey` precisa incluir `employeeId`.
 *
 * Generic `TId = string`: id do filho é a `dataVigencia` (ISO `YYYY-MM-DD`).
 */
export function useEmployeeTaxasHooks(employeeId: number): CrudHooks<
  EmployeeTaxaListItem,
  EmployeeTaxaDto,
  EmployeeTaxaInput,
  string
> & {
  api: CrudApi<EmployeeTaxaListItem, EmployeeTaxaDto, EmployeeTaxaInput, string>;
} {
  const taxasApi = useMemo(() => createEmployeeTaxasApi(employeeId), [employeeId]);
  const queryKey = useMemo(() => ['employees', employeeId, 'taxas'] as const, [employeeId]);
  const qc = useQueryClient();

  const useList = (
    query: DataTableQuery,
  ): UseQueryResult<ListEnvelope<EmployeeTaxaListItem>, Error> =>
    useQuery({
      queryKey: [...queryKey, 'list', query],
      queryFn: () => taxasApi.list(query),
      placeholderData: (previous) => previous,
    });

  const useDetail = (
    dataVigencia: string | null | undefined,
  ): UseQueryResult<EmployeeTaxaDto, Error> =>
    useQuery({
      queryKey: [...queryKey, 'detail', dataVigencia],
      queryFn: () => taxasApi.get(dataVigencia as string),
      enabled: dataVigencia !== null && dataVigencia !== undefined && dataVigencia !== '',
    });

  const useCreate = (): UseMutationResult<EmployeeTaxaDto, Error, EmployeeTaxaInput> =>
    useMutation({
      mutationFn: (input) => taxasApi.create(input),
      // Invalida tanto a lista de Taxas quanto o Colaborador principal — Get do
      // employee deriva 5 campos da Taxa vigente, então mudar Taxa muda o DTO pai.
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: ['employees', employeeId, 'detail'] });
      },
    });

  const useUpdate = (): UseMutationResult<
    EmployeeTaxaDto,
    Error,
    { id: string; input: EmployeeTaxaInput }
  > =>
    useMutation({
      mutationFn: ({ id, input }) => taxasApi.update(id, input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: ['employees', employeeId, 'detail'] });
      },
    });

  const useDelete = (): UseMutationResult<void, Error, string> =>
    useMutation({
      mutationFn: (dataVigencia) => taxasApi.remove(dataVigencia),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey });
        qc.invalidateQueries({ queryKey: ['employees', employeeId, 'detail'] });
      },
    });

  return { api: taxasApi, useList, useDetail, useCreate, useUpdate, useDelete };
}
